import React, { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Form, Input, Select, Space, Spin, Tag } from "antd";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  FileExcelOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  MessageOutlined,
  SendOutlined,
  TeamOutlined,
  UploadOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import PhoneInput from "react-phone-input-2";
import { useNavigate } from "react-router-dom";
import InsideHeader from "../../components/InsideHeader";
import {
  clearWhatsAppQueueResult,
  fetchWhatsAppRegistrations,
  fetchWhatsAppQueueMessage,
  sendMultipleWhatsApp,
  sendSingleWhatsApp,
  uploadWhatsAppExcel,
} from "../../features/whatsapp/whatsappSlice";
import {
  extractErrorMessage,
  normalizeRecipient,
  parseRecipients,
  statusColor,
} from "./whatsappUtils";
import "./whatsapp.css";

const { TextArea } = Input;
const WHATSAPP_TEMPLATE_TEXT = "Dear customer, {{message}} Thank you.";

function buildTemplatePreview(message) {
  const trimmedMessage = String(message || "").trim();
  if (!trimmedMessage) {
    return WHATSAPP_TEMPLATE_TEXT;
  }

  return `Dear customer, ${trimmedMessage} Thank you.`;
}

const WhatsAppSend = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    saving,
    lastQueueResult,
    loadingRegistrations = false,
    registrations = [],
  } = useSelector((state) => state.whatsapp || {});
  const [sendMode, setSendMode] = useState("single");
  const [singleRecipient, setSingleRecipient] = useState("");
  const [uploadFile, setUploadFile] = useState(null);

  const recipientsRaw = Form.useWatch("recipientsRaw", form);
  const messageText = Form.useWatch("text", form);
  const previewUrl = Form.useWatch("previewUrl", form);
  const businessContext = Form.useWatch("businessContext", form);
  const senderRegistrationId = Form.useWatch("senderRegistrationId", form);
  const recipientPreview = parseRecipients(recipientsRaw);
  const singleRecipientNormalized = normalizeRecipient(singleRecipient);
  const finalTemplatePreview = buildTemplatePreview(messageText);
  const senderRegistrations = Array.isArray(registrations) ? registrations : [];
  const senderRegistrationOptions = senderRegistrations.map((item) => ({
    label: `${item?.displayName || "WhatsApp number"} · ${item?.phoneNumber || "-"}`,
    value: item?.id,
  }));
  const selectedSenderRegistration = senderRegistrations.find((item) => item?.id === senderRegistrationId);
  const recipientCount = sendMode === "single" ? (singleRecipientNormalized ? 1 : 0) : recipientPreview.length;
  const characterCount = String(messageText || "").length;
  const wordCount = String(messageText || "").trim()
    ? String(messageText || "")
        .trim()
        .split(/\s+/).length
    : 0;
  const queueStatus = String(lastQueueResult?.status || "").toUpperCase();
  const queueReference = lastQueueResult?.wamid || lastQueueResult?.id || "-";
  const queueAlertType =
    queueStatus === "FAILED"
      ? "error"
      : ["PENDING", "PROCESSING"].includes(queueStatus)
        ? "info"
        : "success";

  useEffect(() => {
    return () => {
      dispatch(clearWhatsAppQueueResult());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchWhatsAppRegistrations());
  }, [dispatch]);

  useEffect(() => {
    if (!lastQueueResult?.id) {
      return undefined;
    }

    const currentStatus = String(lastQueueResult.status || "").toUpperCase();
    if (!["PENDING", "PROCESSING"].includes(currentStatus)) {
      return undefined;
    }

    dispatch(fetchWhatsAppQueueMessage(lastQueueResult.id));

    const intervalId = window.setInterval(() => {
      dispatch(fetchWhatsAppQueueMessage(lastQueueResult.id));
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [dispatch, lastQueueResult?.id, lastQueueResult?.status]);

  const handleSendModeChange = (nextMode) => {
    setSendMode(nextMode);
    setSingleRecipient("");
    dispatch(clearWhatsAppQueueResult());
    form.setFieldsValue({
      recipient: undefined,
      recipientsRaw: "",
    });
  };

  const buildPayload = (values) => {
    return {
      messageType: "TEXT",
      businessContext: values.businessContext,
      text: values.text,
      previewUrl: Boolean(values.previewUrl),
      senderRegistrationId: values.senderRegistrationId || undefined,
    };
  };

  const onFinish = async (values) => {
    const payload = buildPayload(values);
    const action =
      sendMode === "single"
        ? sendSingleWhatsApp({
            ...payload,
            recipient: normalizeRecipient(singleRecipient || values.recipient),
          })
        : sendMultipleWhatsApp({
            ...payload,
            recipients: parseRecipients(values.recipientsRaw),
          });

    const result = await dispatch(action);

    if (sendSingleWhatsApp.fulfilled.match(result) || sendMultipleWhatsApp.fulfilled.match(result)) {
      const queuedResult = result.payload?.data?.result;
      const rejectedCount = queuedResult?.rejectedCount || 0;
      toast[rejectedCount > 0 ? "error" : "success"](result.payload?.messages?.message || "WhatsApp message queued");
      if (sendMode === "single" || rejectedCount === 0) {
        form.resetFields(["text", "recipientsRaw", "previewUrl", "businessContext"]);
        setSingleRecipient("");
      }
      return;
    }

    toast.error(extractErrorMessage(result.payload));
  };

  const queuedCount = lastQueueResult?.acceptedCount ?? (lastQueueResult?.id ? 1 : 0);
  const rejectedCount = lastQueueResult?.rejectedCount ?? 0;

  const handleExcelUpload = async () => {
    if (!uploadFile) {
      toast.error("Please attach an Excel file");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);
    if (senderRegistrationId) {
      formData.append("senderRegistrationId", senderRegistrationId);
    }
    const result = await dispatch(uploadWhatsAppExcel(formData));

    if (uploadWhatsAppExcel.fulfilled.match(result)) {
      const uploadResult = result.payload?.data?.result;
      const uploadRejectedCount = uploadResult?.rejectedCount || 0;
      toast[uploadRejectedCount > 0 ? "error" : "success"](
        result.payload?.messages?.message || "WhatsApp spreadsheet queued"
      );
      if (uploadRejectedCount === 0) {
        setUploadFile(null);
      }
      return;
    }

    toast.error(extractErrorMessage(result.payload));
  };

  const previewRecipients =
    sendMode === "single"
      ? singleRecipientNormalized
        ? [singleRecipientNormalized]
        : []
      : recipientPreview.slice(0, 4);

  return (
    <div className="w-full whatsapp-page whatsapp-send-page">
      <InsideHeader
        title="WhatsApp Send"
        subtitle="Queue one message, a pasted list, or an Excel upload"
        back={true}
        compact
      />

      <div className="whatsapp-shell whatsapp-send-shell">
        <section className="whatsapp-panel whatsapp-send-panel">
          <div className="whatsapp-send-hero">
            <div>
              <div className="whatsapp-kicker">Text-first composer</div>
              <h1>Build a cleaner WhatsApp send</h1>
              <p>
                Choose how you want to send, review the live preview, then queue the
                message into the outbound workflow.
              </p>
            </div>
            <div className="whatsapp-send-hero-pills">
              <span className="whatsapp-send-pill">
                <MessageOutlined />
                Text only
              </span>
              <span className="whatsapp-send-pill">
                <ClockCircleOutlined />
                Queue based
              </span>
              <span className="whatsapp-send-pill">
                <FileExcelOutlined />
                Excel ready
              </span>
            </div>
          </div>

          <div className="whatsapp-send-mode-grid" role="tablist" aria-label="WhatsApp send mode">
            <button
              type="button"
              className={`whatsapp-send-mode-card ${sendMode === "single" ? "is-active" : ""}`}
              onClick={() => handleSendModeChange("single")}
            >
              <span className="whatsapp-send-mode-icon">
                <WhatsAppOutlined />
              </span>
              <span className="whatsapp-send-mode-copy">
                <strong>Single recipient</strong>
                <span>Best for one-off customer follow-up or quick outreach.</span>
              </span>
            </button>

            <button
              type="button"
              className={`whatsapp-send-mode-card ${sendMode === "multiple" ? "is-active" : ""}`}
              onClick={() => handleSendModeChange("multiple")}
            >
              <span className="whatsapp-send-mode-icon">
                <TeamOutlined />
              </span>
              <span className="whatsapp-send-mode-copy">
                <strong>Paste multiple recipients</strong>
                <span>Use commas, semicolons, or one number per line.</span>
              </span>
            </button>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              previewUrl: false,
            }}
          >
            <div className="whatsapp-send-form-grid">
              <div className="whatsapp-send-column">
                <section className="whatsapp-send-section">
                  <div className="whatsapp-send-section-heading">
                    <div>
                      <h2>Recipients</h2>
                      <p>
                        {sendMode === "single"
                          ? "Enter one mobile number. We normalize the number before queueing."
                          : "Paste a list of mobile numbers. Duplicates are removed automatically."}
                      </p>
                    </div>
                    <Tag color={recipientCount > 0 ? "green" : "default"}>
                      {recipientCount} selected
                    </Tag>
                  </div>

                  {sendMode === "single" ? (
                    <Form.Item
                      name="recipient"
                      label="Recipient"
                      rules={[
                        {
                          validator: () =>
                            normalizeRecipient(singleRecipient).length >= 8
                              ? Promise.resolve()
                              : Promise.reject(new Error("Please enter a valid recipient")),
                        },
                      ]}
                    >
                      <PhoneInput
                        country="ke"
                        countryCodeEditable={false}
                        inputClass="whatsapp-phone-input"
                        containerClass="whatsapp-phone"
                        value={singleRecipient}
                        onChange={(value) => setSingleRecipient(String(value))}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="recipientsRaw"
                      label="Recipients"
                      rules={[
                        {
                          validator: (_, value) =>
                            parseRecipients(value).length > 0
                              ? Promise.resolve()
                              : Promise.reject(new Error("Please add at least one recipient")),
                        },
                      ]}
                    >
                      <TextArea
                        rows={6}
                        placeholder={"254700000000\n254711111111\n254722222222"}
                        className="input-textarea"
                      />
                    </Form.Item>
                  )}

                  {sendMode === "multiple" && recipientPreview.length > 0 && (
                    <div className="whatsapp-recipient-preview-list">
                      {previewRecipients.map((recipient) => (
                        <Tag key={recipient} color="blue">
                          {recipient}
                        </Tag>
                      ))}
                      {recipientPreview.length > previewRecipients.length && (
                        <Tag color="default">+{recipientPreview.length - previewRecipients.length} more</Tag>
                      )}
                    </div>
                  )}
                </section>

                <section className="whatsapp-send-section">
                  <div className="whatsapp-send-section-heading">
                    <div>
                      <h2>Message</h2>
                      <p>Write the core message. We will place it inside the approved WhatsApp template below.</p>
                    </div>
                    <div className="whatsapp-send-meta-pills">
                      <span>{characterCount} chars</span>
                      <span>{wordCount} words</span>
                    </div>
                  </div>

                  <Form.Item
                    name="text"
                    label="Message body"
                    rules={[{ required: true, message: "Please enter a WhatsApp message" }]}
                  >
                    <TextArea
                      rows={8}
                      className="input-textarea"
                      placeholder="Hello customer, your order is ready for collection."
                    />
                  </Form.Item>

                  <div className="whatsapp-template-live-card">
                    <span className="whatsapp-template-live-label">Template in use</span>
                    <strong>{WHATSAPP_TEMPLATE_TEXT}</strong>
                    <p>Final customer message preview:</p>
                    <div className="whatsapp-template-live-preview">{finalTemplatePreview}</div>
                  </div>
                </section>
              </div>

              <div className="whatsapp-send-column whatsapp-send-side-column">
                <section className="whatsapp-send-section">
                  <div className="whatsapp-send-section-heading">
                    <div>
                      <h2>WhatsApp number</h2>
                      <p>Select a registered sender number, or leave this blank to use the default WhatsApp number.</p>
                    </div>
                  </div>

                  <Form.Item name="senderRegistrationId" label="Sender number" className="whatsapp-inline-form-item">
                    <Select
                      allowClear
                      loading={loadingRegistrations}
                      placeholder="Use default WhatsApp number"
                      options={senderRegistrationOptions}
                    />
                  </Form.Item>

                  {selectedSenderRegistration ? (
                    <div className="whatsapp-send-context-note">
                      <WhatsAppOutlined />
                      <span>
                        Sending via <strong>{selectedSenderRegistration.displayName}</strong> on{" "}
                        <strong>{selectedSenderRegistration.phoneNumber}</strong>.
                      </span>
                    </div>
                  ) : (
                    <div className="whatsapp-send-context-note">
                      <InfoCircleOutlined />
                      <span>No sender selected, so the default WhatsApp number will be used.</span>
                    </div>
                  )}
                </section>

                <section className="whatsapp-send-section">
                  <div className="whatsapp-send-section-heading">
                    <div>
                      <h2>Live preview</h2>
                      <p>Review the message as the operator prepares it.</p>
                    </div>
                  </div>

                  <div className="whatsapp-preview-shell">
                    <div className="whatsapp-preview-meta">
                      <span>
                        <WhatsAppOutlined />
                        {recipientCount === 1 ? "1 recipient" : `${recipientCount} recipients`}
                      </span>
                      <span>
                        <LinkOutlined />
                        {previewUrl ? "Links preview on" : "Links preview off"}
                      </span>
                    </div>

                    <div className="whatsapp-preview-phone">
                      <div className="whatsapp-preview-bubble">
                        <span className={!String(messageText || "").trim() ? "whatsapp-preview-placeholder" : ""}>
                          {finalTemplatePreview}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="whatsapp-send-section">
                  <div className="whatsapp-send-section-heading">
                    <div>
                      <h2>Delivery options</h2>
                      <p>Small settings that help operations teams keep sends organized.</p>
                    </div>
                  </div>

                  <div className="whatsapp-send-option-grid">
                    <Form.Item name="previewUrl" valuePropName="checked" className="whatsapp-inline-form-item">
                      <Checkbox>Preview links in WhatsApp</Checkbox>
                    </Form.Item>

                    <Form.Item name="businessContext" label="Business context" className="whatsapp-inline-form-item">
                      <Input placeholder="billing, onboarding" />
                    </Form.Item>
                  </div>

                  {businessContext && (
                    <div className="whatsapp-send-context-note">
                      <InfoCircleOutlined />
                      <span>This send will be tagged under `{businessContext}` for later reference.</span>
                    </div>
                  )}
                </section>

                <section className="whatsapp-send-section">
                  <div className="whatsapp-send-section-heading">
                    <div>
                      <h2>Ready to queue</h2>
                      <p>Double-check the basics, then hand it off to the outbound queue.</p>
                    </div>
                  </div>

                  <div className="whatsapp-send-summary-grid">
                    <div>
                      <span>Recipients</span>
                      <strong>{recipientCount}</strong>
                    </div>
                    <div>
                      <span>Words</span>
                      <strong>{wordCount}</strong>
                    </div>
                    <div>
                      <span>Mode</span>
                      <strong>{sendMode === "single" ? "Single" : "Multiple"}</strong>
                    </div>
                    <div>
                      <span>Sender</span>
                      <strong>{selectedSenderRegistration?.displayName || "Default"}</strong>
                    </div>
                    <div>
                      <span>Preview</span>
                      <strong>{previewUrl ? "On" : "Off"}</strong>
                    </div>
                  </div>

                  <div className="whatsapp-actions whatsapp-send-submit-row">
                    <Button
                      htmlType="submit"
                      type="primary"
                      icon={saving ? null : <SendOutlined />}
                      disabled={saving}
                      className="whatsapp-submit whatsapp-send-submit"
                    >
                      {saving ? <Spin size="small" /> : "Queue Message"}
                    </Button>
                  </div>
                </section>
              </div>
            </div>

            <section className="whatsapp-send-section whatsapp-send-upload-section">
              <div className="whatsapp-send-section-heading">
                <div>
                  <h2>Bulk upload via Excel</h2>
                  <p>Queue WhatsApp text messages from a spreadsheet when you already have the list prepared.</p>
                </div>
                <Tag color="green">Optional</Tag>
              </div>

              <div className="whatsapp-upload-specs">
                <div>
                  <strong>Column A</strong>
                  <span>`mobile`</span>
                </div>
                <div>
                  <strong>Column B</strong>
                  <span>`message`</span>
                </div>
              </div>

              <div className="whatsapp-send-context-note whatsapp-upload-template-note">
                <InfoCircleOutlined />
                <span>
                  Each Excel `message` value will be inserted into: <strong>{WHATSAPP_TEMPLATE_TEXT}</strong>
                </span>
              </div>

              <label className="whatsapp-upload-dropzone">
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  style={{ display: "none" }}
                  onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
                />
                <span className="whatsapp-upload-dropzone-icon">
                  <UploadOutlined />
                </span>
                <span className="whatsapp-upload-dropzone-copy">
                  <strong>{uploadFile?.name || "Choose an Excel file"}</strong>
                  <span>Attach `.xls` or `.xlsx` and queue the rows in one go.</span>
                </span>
              </label>

              <div className="whatsapp-upload-actions">
                <Button
                  type="default"
                  onClick={() => setUploadFile(null)}
                  disabled={!uploadFile || saving}
                >
                  Clear
                </Button>

                <Button
                  type="primary"
                  onClick={handleExcelUpload}
                  disabled={saving}
                  icon={saving ? null : <InboxOutlined />}
                >
                  {saving ? <Spin size="small" /> : "Upload to Queue"}
                </Button>
              </div>
            </section>
          </Form>
        </section>

        <aside className="whatsapp-result-panel whatsapp-send-result-panel">
          <div className="whatsapp-result-icon">
            <WhatsAppOutlined />
          </div>
          <h2>Latest queue result</h2>
          <p className="whatsapp-result-intro">
            Track the most recent send action from this page and jump straight into the queue when you need more detail.
          </p>

          <div className="whatsapp-result-links">
            <Button onClick={() => navigate("/whatsapp-queue")}>Open Queue</Button>
            <Button onClick={() => navigate("/whatsapp-dashboard")}>Analytics</Button>
          </div>

          {lastQueueResult ? (
            <Space direction="vertical" size={14} className="whatsapp-result-space">
              <div className="whatsapp-stat-grid">
                <div>
                  <span>Queued</span>
                  <strong>{queuedCount}</strong>
                </div>
                <div>
                  <span>Rejected</span>
                  <strong>{rejectedCount}</strong>
                </div>
              </div>

              {lastQueueResult?.status && (
                <>
                  <div className="whatsapp-result-status-row">
                    <span>Current status</span>
                    <Tag color={statusColor(queueStatus)}>{queueStatus}</Tag>
                  </div>
                  <Alert
                    type={queueAlertType}
                    showIcon
                    message={lastQueueResult.status}
                    description={queueReference}
                  />
                </>
              )}

              {lastQueueResult?.rejected?.length > 0 && (
                <div className="whatsapp-rejected-list">
                  {lastQueueResult.rejected.map((item) => {
                    const message = typeof item === "string" ? item : item?.recipient;
                    const description = typeof item === "string" ? undefined : item?.reason;

                    return (
                      <Alert
                        key={message}
                        type="error"
                        showIcon
                        message={message}
                        description={description}
                      />
                    );
                  })}
                </div>
              )}
            </Space>
          ) : (
            <div className="whatsapp-empty-result">
              <span>Queue a send to see status, IDs, and rejected recipients here.</span>
            </div>
          )}

          <div className="whatsapp-send-tips">
            <div className="whatsapp-send-tip">
              <CheckCircleFilled />
              <span>Single mode is fastest when you are following up one customer.</span>
            </div>
            <div className="whatsapp-send-tip">
              <TeamOutlined />
              <span>Multiple mode accepts comma, semicolon, and line-separated lists.</span>
            </div>
            <div className="whatsapp-send-tip">
              <FileExcelOutlined />
              <span>Use Excel upload when each recipient needs a row-level message.</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default WhatsAppSend;
